import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiLoader, FiPackage, FiLayers, FiChevronDown, FiChevronUp } from "react-icons/fi";
import Card from "../../../../../../components/ui/Card";
import Badge from "../../../../../../components/ui/Badge";
import { ItemIngredientWithDetails } from "../../../../../../types/ingredient";
import { ingredientService } from "../../../../../../services/ingredientService";
import { motion } from "framer-motion";

interface IngredientsInfoSectionProps {
    itemId: string;
    cardVariants?: any;
}

const IngredientsInfoSection: React.FC<IngredientsInfoSectionProps> = ({
    itemId,
    cardVariants,
}) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === "ar";
    const [ingredients, setIngredients] = useState<ItemIngredientWithDetails[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    useEffect(() => {
        const fetchIngredients = async () => {
            try {
                setIsLoading(true);
                const data = await ingredientService.getItemIngredients(itemId);
                setIngredients(data);
                setError(null);
            } catch (err) {
                console.error("Error loading item ingredients:", err);
                setError(t("menuItems.errors.ingredientsFetchFailed") || "Failed to load ingredients");
            } finally {
                setIsLoading(false);
            }
        };

        if (itemId) {
            fetchIngredients();
        }
    }, [itemId, t]);

    if (isLoading) {
        return (
            <Card className="p-4 sm:p-6 border-none shadow-md">
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                        <FiPackage className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t("menuItems.ingredients")}
                    </h3>
                </div>

                <div className="py-12 text-center">
                    <div className="inline-flex items-center justify-center">
                        <FiLoader className="w-8 h-8 sm:w-10 sm:h-10 text-orange-600 dark:text-orange-400 animate-spin" />
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base font-medium mt-4">
                        {t("common.loading")}
                    </p>
                </div>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="p-4 sm:p-6 border-none shadow-md">
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                        <FiPackage className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t("menuItems.ingredients")}
                    </h3>
                </div>

                <div className="py-12 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-red-100 dark:bg-red-900/20 rounded-full mb-4">
                        <svg
                            className="w-8 h-8 sm:w-10 sm:h-10 text-red-500 dark:text-red-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    </div>
                    <p className="text-red-600 dark:text-red-400 text-sm sm:text-base font-medium">
                        {error}
                    </p>
                </div>
            </Card>
        );
    }

    if (ingredients.length === 0) {
        return (
            <Card className="p-0 border-none shadow-lg bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 overflow-hidden">
                {/* Enhanced Header */}
                <div className="relative p-6 ">

                    <div className="relative flex items-center gap-4">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                            <FiPackage className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold mb-1">
                                {t("menuItems.ingredients")}
                            </h3>
                            <p className="text-white/80 text-sm">
                                {t("menuItems.noIngredientsYet")}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Empty State Content */}
                <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/40 dark:to-amber-900/40 rounded-full mx-auto mb-4 flex items-center justify-center">
                        <FiPackage className="w-8 h-8 text-orange-500 dark:text-orange-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        {t("menuItems.noIngredientsYet")}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                        {t("menuItems.ingredientsWillAppearHere", "Ingredients and ingredient groups will appear here when added to this menu item.")}
                    </p>
                </div>
            </Card>
        );
    }

    // Separate individual ingredients and groups
    const individualIngredients = ingredients.filter((item) => item.ingredient);
    const ingredientGroups = ingredients.filter((item) => item.group);

    const toggleGroupExpansion = (groupId: string) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
    };

    return (
        <Card className="p-0 border-none shadow-lg bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 overflow-hidden">
            {/* Enhanced Header */}
            <div className="relative p-6 ">
                {/* Background Pattern */}
                <div className="absolute inset-0 bg-white/10  opacity-50"></div>

                <div className="relative flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                        <FiPackage className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-white mb-1">
                            {t("menuItems.ingredients")}
                        </h3>
                        <p className="text-white/80 text-sm">
                            {ingredients.length === 0
                                ? t("menuItems.noIngredientsYet")
                                : `${ingredients.length} ${t(ingredients.length === 1 ? "menuItems.ingredient" : "menuItems.ingredients")}`
                            }
                        </p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-6">
                <div className="space-y-8">
                    {/* Individual Ingredients Section */}
                    {individualIngredients.length > 0 && (
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center shadow-md">
                                    <FiPackage className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                                        {t("menuItems.individualIngredients", "Individual Ingredients")}
                                    </h4>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {individualIngredients.length} {t(individualIngredients.length === 1 ? "menuItems.ingredient" : "menuItems.ingredients")}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {individualIngredients.map((item) => (
                                    <div
                                        key={item.id}
                                        className={`group relative bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 dark:from-orange-900/20 dark:via-amber-900/20 dark:to-yellow-900/20 border border-orange-200 dark:border-orange-700 rounded-xl p-4 hover:shadow-lg hover:shadow-orange-100 dark:hover:shadow-orange-900/20 hover:-translate-y-1 transition-all duration-200 ${isRTL ? "text-right" : "text-left"
                                            }`}
                                        style={{ direction: isRTL ? "rtl" : "ltr" }}
                                    >


                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 bg-gradient-to-br from-orange-200 to-amber-200 dark:from-orange-800 dark:to-amber-800 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <FiPackage className="w-4 h-4 text-orange-700 dark:text-orange-300" />
                                            </div>
                                            <h5 className="font-semibold text-gray-800 dark:text-white text-sm leading-tight">
                                                {item.ingredient?.name}
                                            </h5>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Ingredient Groups Section */}
                    {ingredientGroups.length > 0 && (
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center shadow-md">
                                    <FiLayers className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                                        {t("menuItems.ingredientGroups", "Ingredient Groups")}
                                    </h4>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {ingredientGroups.length} {t(ingredientGroups.length === 1 ? "menuItems.group" : "menuItems.groups", "groups")}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {ingredientGroups.map((item) => {
                                    const groupId = item.group?.id || item.id;
                                    const isExpanded = expandedGroups.has(groupId);
                                    // Access group_ingredients from the database query result
                                    const groupIngredients = (item.group as any)?.group_ingredients || [];
                                    const ingredientsCount = groupIngredients.length;

                                    return (
                                        <div
                                            key={item.id}
                                            className={`group relative bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 dark:from-purple-900/20 dark:via-indigo-900/20 dark:to-blue-900/20 border-2 border-purple-200 dark:border-purple-700 rounded-xl hover:shadow-xl hover:shadow-purple-100 dark:hover:shadow-purple-900/20 transition-all duration-300 ${isRTL ? "text-right" : "text-left"
                                                }`}
                                            style={{ direction: isRTL ? "rtl" : "ltr" }}
                                        >

                                            {/* Group Header - Clickable */}
                                            <button
                                                onClick={() => toggleGroupExpansion(groupId)}
                                                className={`w-full p-5 focus:outline-none focus:ring-2 focus:ring-purple-300 rounded-xl transition-all duration-200 ${isRTL ? "text-right" : "text-left"}`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-gradient-to-br from-purple-200 via-indigo-200 to-blue-200 dark:from-purple-800 dark:via-indigo-800 dark:to-blue-800 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-200">
                                                            <FiLayers className="w-5 h-5 text-purple-700 dark:text-purple-300" />
                                                        </div>
                                                        <div>
                                                            <h5 className="font-bold text-gray-800 dark:text-white text-base leading-tight mb-1">
                                                                {item.group?.name}
                                                            </h5>
                                                            <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                                                                {ingredientsCount} {t(ingredientsCount === 1 ? "menuItems.ingredient" : "menuItems.ingredients", "ingredients")}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <Badge
                                                            variant="secondary"
                                                            className="bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-semibold px-2 py-1"
                                                        >
                                                            {ingredientsCount}
                                                        </Badge>
                                                        <div className={`p-2 rounded-full bg-purple-100 dark:bg-purple-900/40 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}>
                                                            <FiChevronDown className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>

                                            {/* Expandable Content */}
                                            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                                                <div className="px-5 pb-5 pt-2 border-t border-purple-200 dark:border-purple-700/50">
                                                    {groupIngredients && groupIngredients.length > 0 ? (
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                                                            {groupIngredients.map((groupIngredient: any, idx: number) => (
                                                                <div
                                                                    key={idx}
                                                                    className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800/50 rounded-lg border border-purple-100 dark:border-purple-800/50 hover:shadow-md transition-all duration-200"
                                                                >
                                                                    <div className="w-6 h-6 bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900 dark:to-indigo-900 rounded-md flex items-center justify-center flex-shrink-0">
                                                                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                                                    </div>
                                                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 leading-tight">
                                                                        {groupIngredient?.ingredient?.name}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-4">
                                                            <p className="text-sm text-purple-500 dark:text-purple-400 italic">
                                                                {t("menuItems.noIngredientsInGroup", "No ingredients in this group")}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default IngredientsInfoSection;
